import { useState, useRef, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  createTournament,
  selectTournament,
  uploadTournamentLogo,
  getSelectedTournament,
} from "../../redux/slices/tournamentSlice";
import {
  createOrganisation,
  selectOrganisation,
} from "../../redux/slices/organisationSlice";
import { useTournamentCreation } from "../../context/TournamentCreationContext";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import { competitionCreated } from "../../utils/analytics";

const CompetitionVenueRefactored = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formData, updateField, updateFields, getCreatePayload, resetForm } = useTournamentCreation();
  const selectedOrganisation = useSelector(
    (state) => state.organisation.selectedOrganisation
  );

  // Local UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocationName, setSelectedLocationName] = useState("");

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Derive from context
  const venueName = formData.venue;
  const numberOfPitches = formData.numberOfPitches || "1";
  const selectedLocation = formData.location.lat !== null ? formData.location : null;

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  // Guard: redirect to step 1 if required prior-step data is missing (e.g. user refreshed)
  useEffect(() => {
    if (!formData.name || !formData.startDate || !formData.endDate || !formData.sport) {
      navigate(DASHBOARD_STACK.COMPETITIONS, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const onPlaceSelected = (place) => {
    if (!place.geometry || !place.geometry.location) return;

    const loc = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    const link = `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
    updateFields({ location: loc, mapLink: link });
    setSearchQuery(place.formatted_address);
    setSelectedLocationName(place.formatted_address);

    if (mapRef.current) {
      mapRef.current.panTo(loc);
      mapRef.current.setZoom(15);
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const loc = { lat, lng };
    const link = `https://www.google.com/maps?q=${lat},${lng}`;
    updateFields({ location: loc, mapLink: link });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: loc }, (results, status) => {
      if (status === "OK" && results[0]) {
        setSelectedLocationName(results[0].formatted_address);
      }
    });
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    if (!venueName.trim()) return toast.error("Please enter venue name");
    if (!selectedLocation) return toast.error("Please select a location");

    if (!selectedOrganisation?.id) {
      toast.error("Please select an organisation first");
      return;
    }

    setIsLoading(true);
    try {
      // Handle mock organisation
      let orgIdToUse = selectedOrganisation.id;
      if (orgIdToUse === "default-org-1") {
        try {
          const orgResult = await dispatch(
            createOrganisation({ name: "Default Organisation" })
          ).unwrap();
          orgIdToUse = orgResult.id;
          dispatch(selectOrganisation({ ...orgResult, isSelected: true }));
        } catch (err) {
          toast.error("Failed to initialize default organisation");
          setIsLoading(false);
          return;
        }
      }

      // Create tournament
      const payload = getCreatePayload(orgIdToUse);
      const createResult = await dispatch(createTournament(payload));

      if (createResult.meta.requestStatus === "rejected") {
        throw new Error(createResult.payload || "Failed to create competition");
      }

      const newTournament = createResult.payload;
      dispatch(selectTournament(newTournament));

      competitionCreated({
        competitionId: newTournament?.id || newTournament?._id,
        sport: newTournament?.sport,
        categoryCount: newTournament?.categories?.length,
      });
      // Meta Pixel: competition-created is the activation conversion for ads.
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'CompetitionCreated', {
          tournament_id: newTournament?.id || newTournament?._id,
        });
      }

      if (selectedOrganisation?.id) {
        await dispatch(getSelectedTournament(selectedOrganisation.id));
      }

      // Upload logo if provided
      if (formData.logoFile) {
        try {
          const logoFormData = new FormData();
          logoFormData.append("logo", formData.logoFile);
          await dispatch(
            uploadTournamentLogo({
              tournamentId: newTournament.id,
              file: logoFormData,
            })
          ).unwrap();
        } catch (err) {}
      }

      resetForm();
      toast.success(`Competition "${formData.name}" created!`);
      navigate(DASHBOARD_STACK.HOME);
    } catch (error) {
      toast.error(error.message || "Failed to create competition");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full bg-[#000F1F] text-[#FEFCEB] flex flex-1 flex-col items-center overflow-hidden">
      <div className="w-full max-w-2xl flex-1 min-h-0 flex flex-col items-center py-6">
        {/* Header */}
        <div className="flex flex-col gap-[16px] items-center justify-center shrink-0">
          <h1 className="text-[24px] sm:text-[32px] md:text-[40px] font-medium text-[#FEFCEB] text-center tracking-[1px]">Create new competition</h1>
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-3 sm:gap-[28px]">
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] flex items-center justify-center relative">
                <svg className="w-[10px] h-[10px]" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#E5E5D6]">Competition details</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] flex items-center justify-center relative">
                <svg className="w-[10px] h-[10px]" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#E5E5D6]">Configuration</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] text-[#000F1F] flex items-center justify-center font-semibold text-[14px]">3</div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#FEFCEB]">Venue details</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="w-full max-w-[480px] flex-1 min-h-0 my-5 border border-app-border rounded-2xl p-6 flex flex-col gap-[16px]">
          {/* Venue Name */}
          <div className="flex flex-col gap-2 shrink-0">
            <label className="block text-[14px] font-normal text-clubbie-white ">Venue name</label>
            <input
              type="text"
              value={venueName}
              onChange={(e) => updateField("venue", e.target.value)}
              placeholder="Venue name"
              className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
            />
          </div>

          {/* Location */}
          <div className="flex flex-col gap-[16px] flex-1 min-h-0">
            <div className="flex flex-col gap-2 shrink-0">
              <label className="block text-[14px] font-normal text-clubbie-white ">Location</label>
              <input
                type="text"
                value={selectedLocationName}
                readOnly
                placeholder="Location"
                className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary outline-none cursor-default"
              />
            </div>

            {/* Map Container */}
            <div className="relative w-full flex-1 min-h-0 max-h-[480px] rounded-[20px] overflow-hidden border border-[#192634]">
              {!isMapsLoaded ? (
                <div className="w-full h-full flex items-center justify-center text-app-text-secondary text-sm">
                  Loading map...
                </div>
              ) : (
                <>
                {/* Search Overlay */}
                <div className="absolute top-4 left-4 right-14 z-10">
                   <Autocomplete
                    onLoad={(autocomplete) => {
                      autocompleteRef.current = autocomplete;
                    }}
                    onPlaceChanged={() => {
                      if (autocompleteRef.current) {
                        const place = autocompleteRef.current.getPlace();
                        onPlaceSelected(place);
                      }
                    }}
                    options={{
                      types: ["establishment", "geocode"],
                    }}
                  >
                    <div className="relative shadow-lg">
                      <input
                        type="text"
                        placeholder="Search location..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-white text-black rounded-md pl-3 pr-8 py-2 text-sm focus:outline-none"
                      />
                       <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                        </svg>
                      </div>
                    </div>
                  </Autocomplete>
                </div>

                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={selectedLocation || { lat: 51.5074, lng: -0.1278 }}
                  zoom={selectedLocation ? 15 : 10}
                  onClick={handleMapClick}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                  options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    styles: [
                      {
                        featureType: "all",
                        elementType: "geometry",
                        stylers: [{ color: "#242f3e" }],
                      },
                      {
                        featureType: "all",
                        elementType: "labels.text.stroke",
                        stylers: [{ lightness: -80 }],
                      },
                      {
                        featureType: "administrative",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#746855" }],
                      },
                      {
                        featureType: "administrative.locality",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#d59563" }],
                      },
                      {
                        featureType: "poi",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#d59563" }],
                      },
                      {
                        featureType: "poi.park",
                        elementType: "geometry",
                        stylers: [{ color: "#263c3f" }],
                      },
                      {
                        featureType: "poi.park",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#6b9a76" }],
                      },
                      {
                        featureType: "road",
                        elementType: "geometry",
                        stylers: [{ color: "#38414e" }],
                      },
                      {
                        featureType: "road",
                        elementType: "geometry.stroke",
                        stylers: [{ color: "#212a37" }],
                      },
                      {
                        featureType: "road",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#9ca5b3" }],
                      },
                      {
                        featureType: "road.highway",
                        elementType: "geometry",
                        stylers: [{ color: "#746855" }],
                      },
                      {
                        featureType: "road.highway",
                        elementType: "geometry.stroke",
                        stylers: [{ color: "#1f2835" }],
                      },
                      {
                        featureType: "road.highway",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#f3d19c" }],
                      },
                      {
                        featureType: "transit",
                        elementType: "geometry",
                        stylers: [{ color: "#2f3948" }],
                      },
                      {
                        featureType: "transit.station",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#d59563" }],
                      },
                      {
                        featureType: "water",
                        elementType: "geometry",
                        stylers: [{ color: "#17263c" }],
                      },
                      {
                        featureType: "water",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#515c6d" }],
                      },
                      {
                        featureType: "water",
                        elementType: "labels.text.stroke",
                        stylers: [{ lightness: -20 }],
                      },
                    ],
                  }}
                >
                  {selectedLocation && <Marker position={selectedLocation} />}
                </GoogleMap>
                </>
              )}
            </div>
          </div>


        </div>

        {/* Buttons */}
        <div className="w-full max-w-[480px] flex flex-col gap-[12px] items-center justify-center shrink-0 mb-4">
          {/* Create Button */}
          <button
            onClick={handleCreate}
            disabled={isLoading || !venueName.trim() || !selectedLocation}
            className={`w-full h-[44px] px-[16px] py-[8px] rounded-full text-sm font-medium transition-all ${
              isLoading || !venueName.trim() || !selectedLocation
                ? "bg-[#002F41] text-[#021529] cursor-not-allowed"
                : "bg-[#00E7FF] text-[#021529] hover:bg-[#00cce6]"
            }`}
          >
            {isLoading ? "Creating..." : "Create competition"}
          </button>

          {/* Back Link */}
          <button
            onClick={() => navigate(DASHBOARD_STACK.COMPETITIONS_CONFIG)}
            className="h-[32px] px-[8px] py-0 text-app-text-secondary text-sm font-medium rounded-lg hover:text-[#FEFCEB] transition-colors"
          >
            Back to configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetitionVenueRefactored;
